import { createFileRoute, Link } from "@tanstack/react-router";
import { useGetEquipmentById } from "@/api/client-equipment";
import { PageStatus } from "@/components/page-status";
import { Button } from "@/components/ui/button";
import {
  DetailCardSkeleton,
  DetailHeaderSkeleton,
} from "@/components/ui/detail-skeleton";
import { requireMinRole } from "@/lib/route-guards";
import { EquipmentDetailsPage } from "@/sections/equipment/details";

export const Route = createFileRoute("/dashboard/equipment/$equipmentId")({
  component: Component,
  beforeLoad: ({ context }) => {
    requireMinRole(context.session, "viewer");
  },
  staticData: {
    title: "Equipment Details",
    parent: { title: "Equipment", path: "/dashboard/equipment" },
  },
});

// Mirrors EquipmentDetailsPage: sticky header bar + a two-column grid of
// detail cards.
const EQUIPMENT_CARD_KEYS = ["a", "b", "c", "d", "e", "f"];

function EquipmentDetailsSkeleton() {
  return (
    <>
      <DetailHeaderSkeleton subtitle trailingPills={2} />
      <div className="p-4 lg:p-7 pb-24">
        <div className="grid gap-6 md:grid-cols-2">
          {EQUIPMENT_CARD_KEYS.map((k) => (
            <DetailCardSkeleton key={k} rows={4} />
          ))}
        </div>
      </div>
    </>
  );
}

function Component() {
  const { equipmentId } = Route.useParams();
  const {
    data: equipment,
    isLoading,
    error,
    refetch,
  } = useGetEquipmentById(equipmentId);

  if (isLoading) {
    return <EquipmentDetailsSkeleton />;
  }

  if (error || !equipment) {
    return (
      <PageStatus
        variant="error"
        title="Failed to load equipment"
        description="We couldn't load this equipment record. It may have been removed, or there was a problem reaching the server. Try again, or return to the equipment register."
        actions={
          <>
            <Button onClick={() => refetch()}>Try again</Button>
            <Button
              variant="outline"
              nativeButton={false}
              render={<Link to="/dashboard/equipment" />}
            >
              Back to equipment
            </Button>
          </>
        }
      />
    );
  }

  return <EquipmentDetailsPage equipment={equipment} />;
}
